import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CaretLeft, PencilSimple } from "@phosphor-icons/react";
import { Attachments } from "@/components/Attachments";
import { PhotoGallery } from "@/components/PhotoGallery";
import { DrawingList } from "@/components/DrawingList";
// Meetings import removed — MOM tab no longer shown inside ProjectDetail; standalone /meetings route lives in App.js.
import VendorPOs from "@/pages/VendorPOs";
import Inventory from "@/pages/Inventory";
import MaterialRequests from "@/pages/procurement/MaterialRequests";
import EquipmentRegistry from "@/pages/service/EquipmentRegistry";
import Snags from "@/pages/service/Snags";
import ServiceTickets from "@/pages/service/ServiceTickets";
import CommissioningReports from "@/pages/service/CommissioningReports";
import PPMSchedules from "@/pages/service/PPMSchedules";
import ProjectEditSheet from "@/pages/projects/ProjectEditSheet";

function Kpi({ label, value, accent, sub }) {
  return (
    <div className="bg-white border border-slate-200 p-4 rounded-sm">
      <div className="label-caps">{label}</div>
      <div className={`font-display text-2xl font-bold font-mono-num mt-2 ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="label-caps">{title}</div>
        <div className="text-xs text-slate-500">{count} record{count === 1 ? "" : "s"}</div>
      </div>
      {count === 0 ? <div className="p-6 text-sm text-slate-400 text-center">No {title.toLowerCase()} for this project.</div> : children}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = user && (user.role === "admin" || user.role === "manager");
  const [d, setD] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/projects/${id}/detail`);
    setD(r.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="p-8 text-slate-500">Loading…</div>;

  const { project, invoices, payments, expenses, purchases, sales, totals } = d;
  const overBudget = totals.budget > 0 && totals.total_spent > totals.budget;

  return (
    <div data-testid="project-detail-page">
      <PageHeader
        title={project.name}
        subtitle={<span className="flex items-center gap-3"><span>{project.client_name}</span><StatusBadge status={project.status} /></span>}
        actions={
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                data-testid="edit-project-btn"
                className="rounded-sm border-slate-300 hover:border-[#EA580C] hover:text-[#EA580C]"
              >
                <PencilSimple size={14} className="mr-1.5" /> Edit project
              </Button>
            )}
            <Link to="/projects" className="text-sm text-slate-600 hover:text-[#EA580C] flex items-center gap-1"><CaretLeft size={14} /> Back to projects</Link>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Kpi label="Budget" value={fmtMoney(totals.budget)} sub={`${totals.budget_used_pct}% used`} />
          <Kpi label="Total Spent" value={fmtMoney(totals.total_spent)} accent={overBudget ? "text-red-700" : "text-slate-900"} sub={overBudget ? "Over budget" : "Within budget"} />
          <Kpi label="Revenue" value={fmtMoney(totals.revenue)} accent="text-emerald-700" sub={`Received ${fmtMoney(totals.received)}`} />
          <Kpi label="Project Profit" value={fmtMoney(totals.profit)} accent="text-[#EA580C]" />
        </div>

        {totals.budget > 0 && (
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-caps">Budget vs Actual</div>
                <div className="text-sm text-slate-500 mt-1">
                  {fmtMoney(totals.total_spent)} of {fmtMoney(totals.budget)} spent —
                  <span className={overBudget ? "text-red-700 font-semibold ml-1" : "text-emerald-700 font-semibold ml-1"}>
                    {fmtMoney(totals.budget_remaining)} {overBudget ? "over" : "remaining"}
                  </span>
                </div>
              </div>
              <div className={`font-display text-2xl font-bold font-mono-num ${overBudget ? "text-red-700" : "text-[#EA580C]"}`}>
                {totals.budget_used_pct}%
              </div>
            </div>
            <Progress value={Math.min(totals.budget_used_pct, 100)} className="h-2 rounded-none" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="Invoiced" value={fmtMoney(totals.invoiced)} sub={`${invoices.length} invoices`} />
          <Kpi label="Outstanding" value={fmtMoney(totals.outstanding)} accent="text-amber-700" />
          <Kpi label="Direct Sales" value={fmtMoney(totals.direct_sales)} sub={`${sales.length} entries`} />
        </div>

        <Tabs defaultValue="documents" className="w-full" data-testid="project-tabs">
          <div className="relative">
            <div className="overflow-x-auto no-scrollbar" data-testid="project-tabs-scroll">
              <TabsList className="rounded-sm bg-slate-100 p-1 inline-flex whitespace-nowrap w-max min-w-full">
                <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Documents</TabsTrigger>
                <TabsTrigger value="invoices" data-testid="tab-invoices" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Invoices</TabsTrigger>
                <TabsTrigger value="expenses" data-testid="tab-expenses" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Expenses</TabsTrigger>
                <TabsTrigger value="purchases" data-testid="tab-purchases" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Purchases</TabsTrigger>
                <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Payments</TabsTrigger>
                <TabsTrigger value="vendor-pos" data-testid="tab-vendor-pos" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Vendor POs</TabsTrigger>
                <TabsTrigger value="material-requests" data-testid="tab-material-requests" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Material Requests</TabsTrigger>
                <TabsTrigger value="inventory" data-testid="tab-inventory" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Inventory</TabsTrigger>
                <TabsTrigger value="equipment" data-testid="tab-equipment" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Equipment</TabsTrigger>
                <TabsTrigger value="snags" data-testid="tab-snags" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Snags</TabsTrigger>
                <TabsTrigger value="tickets" data-testid="tab-tickets" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Tickets</TabsTrigger>
                <TabsTrigger value="commissioning" data-testid="tab-commissioning" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Commissioning</TabsTrigger>
                <TabsTrigger value="ppm" data-testid="tab-ppm" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">PPM</TabsTrigger>
              </TabsList>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent" />
          </div>

          <TabsContent value="documents" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="project-documents">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="label-caps mb-3">Drawings & Revisions</div>
                <DrawingList parentType="project" parentId={id} category="drawing" />
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="label-caps mb-3">Client Purchase Orders</div>
                <Attachments parentType="project" parentId={id} category="client_po" />
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 lg:col-span-2">
                <div className="label-caps mb-3">Site Photos · with comments</div>
                <PhotoGallery parentType="project" parentId={id} category="photo" />
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4 lg:col-span-2">
                <div className="label-caps mb-3">Other Attachments</div>
                <Attachments parentType="project" parentId={id} category="other" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <Section title="Invoices" count={invoices.length} testid="pd-invoices">
              <InvoicesTable rows={invoices} />
            </Section>
          </TabsContent>

          <TabsContent value="expenses" className="mt-4">
            <Section title="Expenses" count={expenses.length} testid="pd-expenses">
              <ExpensesTable rows={expenses} />
            </Section>
          </TabsContent>

          <TabsContent value="purchases" className="mt-4">
            <Section title="Purchases" count={purchases.length} testid="pd-purchases">
              <PurchasesTable rows={purchases} />
            </Section>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <Section title="Payments Received" count={payments.length} testid="pd-payments">
              <PaymentsTable rows={payments} />
            </Section>
          </TabsContent>

          <TabsContent value="vendor-pos" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <VendorPOs projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="material-requests" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <MaterialRequests projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="inventory" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <Inventory projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="equipment" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <EquipmentRegistry projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="snags" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <Snags projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="tickets" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <ServiceTickets projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="commissioning" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <CommissioningReports projectId={id} embedded />
            </div>
          </TabsContent>

          <TabsContent value="ppm" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <PPMSchedules projectId={id} embedded />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit sheet — admin/manager only */}
      {canEdit && (
        <ProjectEditSheet
          project={project}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ============ Sub-tables for tabs ============
function InvoicesTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50"><tr>
        <th className="p-3 text-left label-caps">#</th><th className="p-3 text-left label-caps">Issue</th>
        <th className="p-3 text-left label-caps">Due</th><th className="p-3 text-left label-caps">Status</th>
        <th className="p-3 text-right label-caps">Total</th><th className="p-3 text-right label-caps">Paid</th>
      </tr></thead>
      <tbody>{rows.map((i) => (
        <tr key={i.id} className="border-t border-slate-100">
          <td className="p-3 font-mono-num">{i.invoice_number}</td>
          <td className="p-3 text-slate-600">{fmtDate(i.issue_date)}</td>
          <td className="p-3 text-slate-600">{fmtDate(i.due_date)}</td>
          <td className="p-3"><StatusBadge status={i.status} /></td>
          <td className="p-3 text-right font-mono-num">{fmtMoney(i.total)}</td>
          <td className="p-3 text-right font-mono-num text-emerald-700">{fmtMoney(i.amount_paid)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function ExpensesTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50"><tr>
        <th className="p-3 text-left label-caps">Date</th><th className="p-3 text-left label-caps">Category</th>
        <th className="p-3 text-left label-caps">Vendor</th><th className="p-3 text-left label-caps">Description</th>
        <th className="p-3 text-right label-caps">Amount</th>
      </tr></thead>
      <tbody>{rows.map((e) => (
        <tr key={e.id} className="border-t border-slate-100">
          <td className="p-3 text-slate-600">{fmtDate(e.date)}</td>
          <td className="p-3 text-xs uppercase tracking-wider text-slate-700">{e.category}</td>
          <td className="p-3 text-slate-600">{e.vendor || "—"}</td>
          <td className="p-3 text-slate-600">{e.description || "—"}</td>
          <td className="p-3 text-right font-mono-num text-red-700">{fmtMoney(e.amount)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function PurchasesTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50"><tr>
        <th className="p-3 text-left label-caps">Date</th><th className="p-3 text-left label-caps">Vendor</th>
        <th className="p-3 text-left label-caps">Item</th><th className="p-3 text-right label-caps">Qty</th>
        <th className="p-3 text-right label-caps">Total</th>
      </tr></thead>
      <tbody>{rows.map((p) => (
        <tr key={p.id} className="border-t border-slate-100">
          <td className="p-3 text-slate-600">{fmtDate(p.date)}</td>
          <td className="p-3">{p.vendor}</td>
          <td className="p-3 text-slate-600">{p.item}</td>
          <td className="p-3 text-right font-mono-num">{p.quantity}</td>
          <td className="p-3 text-right font-mono-num text-red-700">{fmtMoney(p.total)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function PaymentsTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50"><tr>
        <th className="p-3 text-left label-caps">Date</th><th className="p-3 text-left label-caps">Method</th>
        <th className="p-3 text-left label-caps">Reference</th><th className="p-3 text-right label-caps">Amount</th>
      </tr></thead>
      <tbody>{rows.map((p) => (
        <tr key={p.id} className="border-t border-slate-100">
          <td className="p-3 text-slate-600">{fmtDate(p.date)}</td>
          <td className="p-3 text-xs uppercase tracking-wider">{p.method}</td>
          <td className="p-3 text-slate-600">{p.reference || "—"}</td>
          <td className="p-3 text-right font-mono-num text-emerald-700">{fmtMoney(p.amount)}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}
